import {pgTable as table, pgEnum} from "drizzle-orm/pg-core";
import * as t from "drizzle-orm/pg-core";
import {timestamps} from "./schema.helper";

export const fileTypeEnum = pgEnum('file_type', ['THUMBNAIL', 'IMAGE', 'VIDEO', 'OTHER']);
export const relationshipTypeEnum = pgEnum('relationship_type', [
  'SPOUSE',
  'PARTNER',
  'CHILD',
  'PARENT',
  'SIBLING',
  'GRANDPARENT',
  'GRANDCHILD',
  'FRIEND',
  'COWORKER',
  'OTHER',
]);
export const personAccountLinkStatusEnum = pgEnum('person_account_link_status', [
  'UNVERIFIED',
  'VERIFIED',
  'SELF',
  'MERGED',
]);
export const personAccountLinkSourceEnum = pgEnum('person_account_link_source', [
  'LOCAL',
  'INVITE',
  'SYNC',
  'MANUAL',
]);
export const scopeTypeEnum = pgEnum('scope_type', [
  'PERSONAL',
  'HOUSEHOLD',
]);
export const scopeMemberRoleEnum = pgEnum('scope_member_role', [
  'OWNER',
  'ADULT_MEMBER',
  'MEMBER',
  'CHILD_MEMBER',
  'VIEWER',
]);

export const eventsTable = table(
  "events",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    key: t.uuid().defaultRandom(),
    scopeId: t.integer().references(() => scopesTable.id, {onDelete: "set null"}),
    date: t.timestamp(),
    datePrecision: t.varchar({ length: 16 }).$type<import("../event-date").DatePrecision>(),
    dateClarification: t.jsonb().$type<import("../event-date-clarification").DateClarification>(),
    endDate: t.timestamp(),
    parentEventId: t.integer(),
    afterEventEndId: t.integer(),
    locationId: t.integer(),
    title: t.varchar({ length: 256 }),
    description: t.varchar({ length: 512 }),
    text: t.text(),
    importance: t.smallint().default(0),
    originalText: t.text(),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("slug_idx").on(table.key),
    t.index("events_scope_id_idx").on(table.scopeId),
    t.index("title_idx").on(table.title),
    t.index("events_parent_id_idx").on(table.parentEventId),
    t.index("events_after_event_end_id_idx").on(table.afterEventEndId),
    t.index("events_location_id_idx").on(table.locationId),
    t.index("events_date_idx").on(table.date),
    t.foreignKey({
      columns: [table.parentEventId],
      foreignColumns: [table.id],
      name: "events_parent_event_id_fk",
    }).onDelete("set null"),
    t.foreignKey({
      columns: [table.afterEventEndId],
      foreignColumns: [table.id],
      name: "events_after_event_end_id_fk",
    }).onDelete("set null"),
    t.foreignKey({
      columns: [table.locationId],
      foreignColumns: [locationsTable.id],
      name: "events_location_id_fk",
    }).onDelete("set null"),
  ]
);

export const eventFilesTable = table(
  "event_files",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    eventId: t.integer().references(() => eventsTable.id, {onDelete: "cascade"}),
    storageId: t.varchar({length: 255}), // References the file in storage system
    fileType: fileTypeEnum(),
    mimeType: t.varchar({length: 100}),
    ext: t.varchar({length: 100}),
    fileSize: t.numeric({ precision: 10, scale: 2 }),
    filename: t.varchar({length: 255}),
    ...timestamps
  },
  (table) => [
    t.index("event_files_event_id_idx").on(table.eventId),
    t.index("event_files_storage_id_idx").on(table.storageId),
  ]
);

export const scopesTable = table(
  "scopes",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    key: t.uuid().defaultRandom(),
    type: scopeTypeEnum().notNull(),
    name: t.varchar({ length: 120 }),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("scopes_key_idx").on(table.key),
    t.index("scopes_type_idx").on(table.type),
  ]
);

export const accountsTable = table(
  "accounts",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    key: t.uuid().defaultRandom(),
    email: t.varchar({ length: 255 }),
    givenName: t.varchar({ length: 100 }),
    familyName: t.varchar({ length: 100 }),
    displayName: t.varchar({ length: 200 }),
    avatarUrl: t.varchar({ length: 512 }),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("accounts_key_idx").on(table.key),
    t.uniqueIndex("accounts_email_uq_idx").on(table.email),
    t.index("accounts_display_name_idx").on(table.displayName),
  ]
);

export const scopeMembersTable = table(
  "scope_members",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    scopeId: t.integer().references(() => scopesTable.id, {onDelete: "cascade"}).notNull(),
    accountId: t.integer().references(() => accountsTable.id, {onDelete: "cascade"}).notNull(),
    role: scopeMemberRoleEnum().default('MEMBER').notNull(),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("scope_members_scope_account_uq_idx").on(table.scopeId, table.accountId),
    t.index("scope_members_scope_id_idx").on(table.scopeId),
    t.index("scope_members_account_id_idx").on(table.accountId),
    t.index("scope_members_role_idx").on(table.role),
  ]
);

export const peopleTable = table(
  "people",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    key: t.uuid().defaultRandom(),
    scopeId: t.integer().references(() => scopesTable.id, {onDelete: "set null"}),
    firstName: t.varchar({ length: 100 }),
    lastName: t.varchar({ length: 100 }),
    fullName: t.varchar({ length: 200 }),
    birthDate: t.timestamp(),
    notes: t.text(),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("people_key_idx").on(table.key),
    t.index("people_scope_id_idx").on(table.scopeId),
    t.index("people_full_name_idx").on(table.fullName),
    t.index("people_birth_date_idx").on(table.birthDate),
  ]
);

export const personAccountLinksTable = table(
  "person_account_links",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    personId: t.integer().references(() => peopleTable.id, {onDelete: "cascade"}).notNull(),
    accountId: t.integer().references(() => accountsTable.id, {onDelete: "cascade"}).notNull(),
    status: personAccountLinkStatusEnum().default('UNVERIFIED').notNull(),
    source: personAccountLinkSourceEnum().default('LOCAL').notNull(),
    isPrimary: t.boolean().default(false).notNull(),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("person_account_links_person_account_uq_idx").on(table.personId, table.accountId),
    t.index("person_account_links_person_id_idx").on(table.personId),
    t.index("person_account_links_account_id_idx").on(table.accountId),
    t.index("person_account_links_status_idx").on(table.status),
  ]
);

export const personNicknamesTable = table(
  "person_nicknames",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    ownerAccountId: t.integer().references(() => accountsTable.id, {onDelete: "cascade"}).notNull(),
    personId: t.integer().references(() => peopleTable.id, {onDelete: "cascade"}).notNull(),
    nickname: t.varchar({ length: 120 }).notNull(),
    normalizedNickname: t.varchar({ length: 120 }).notNull(),
    isPrimary: t.boolean().default(false).notNull(),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("person_nicknames_owner_person_nickname_uq_idx").on(table.ownerAccountId, table.personId, table.normalizedNickname),
    t.index("person_nicknames_owner_account_id_idx").on(table.ownerAccountId),
    t.index("person_nicknames_person_id_idx").on(table.personId),
    t.index("person_nicknames_normalized_idx").on(table.normalizedNickname),
  ]
);

export const personRelationshipsTable = table(
  "person_relationships",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    ownerAccountId: t.integer().references(() => accountsTable.id, {onDelete: "cascade"}).notNull(),
    subjectPersonId: t.integer().references(() => peopleTable.id, {onDelete: "cascade"}).notNull(),
    targetPersonId: t.integer().references(() => peopleTable.id, {onDelete: "cascade"}).notNull(),
    relationshipType: relationshipTypeEnum().default('OTHER').notNull(),
    relationshipLabel: t.varchar({ length: 80 }),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("person_relationships_owner_subject_target_uq_idx").on(table.ownerAccountId, table.subjectPersonId, table.targetPersonId),
    t.index("person_relationships_owner_account_id_idx").on(table.ownerAccountId),
    t.index("person_relationships_subject_person_id_idx").on(table.subjectPersonId),
    t.index("person_relationships_target_person_id_idx").on(table.targetPersonId),
    t.index("person_relationships_type_idx").on(table.relationshipType),
  ]
);

export const tagsTable = table(
  "tags",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    key: t.uuid().defaultRandom(),
    name: t.varchar({ length: 80 }).notNull(),
    normalizedName: t.varchar({ length: 80 }).notNull(),
    color: t.varchar({ length: 20 }),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("tags_key_idx").on(table.key),
    t.uniqueIndex("tags_normalized_name_uq_idx").on(table.normalizedName),
    t.index("tags_name_idx").on(table.name),
  ]
);

export const locationsTable = table(
  "locations",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    key: t.uuid().defaultRandom(),
    name: t.varchar({ length: 160 }).notNull(),
    normalizedName: t.varchar({ length: 160 }),
    addressLine1: t.varchar({ length: 255 }),
    addressLine2: t.varchar({ length: 255 }),
    city: t.varchar({ length: 120 }),
    region: t.varchar({ length: 120 }),
    postalCode: t.varchar({ length: 40 }),
    country: t.varchar({ length: 120 }),
    latitude: t.numeric({ precision: 10, scale: 7 }),
    longitude: t.numeric({ precision: 10, scale: 7 }),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("locations_key_idx").on(table.key),
    t.index("locations_name_idx").on(table.name),
    t.index("locations_normalized_name_idx").on(table.normalizedName),
  ]
);

export const eventPeopleTable = table(
  "event_people",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    eventId: t.integer().references(() => eventsTable.id, {onDelete: "cascade"}).notNull(),
    personId: t.integer().references(() => peopleTable.id, {onDelete: "cascade"}).notNull(),
    relevance: t.smallint().default(0),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("event_people_event_person_uq_idx").on(table.eventId, table.personId),
    t.index("event_people_event_id_idx").on(table.eventId),
    t.index("event_people_person_id_idx").on(table.personId),
  ]
);

export const eventTagsTable = table(
  "event_tags",
  {
    id: t.integer().primaryKey().generatedAlwaysAsIdentity(),
    eventId: t.integer().references(() => eventsTable.id, {onDelete: "cascade"}).notNull(),
    tagId: t.integer().references(() => tagsTable.id, {onDelete: "cascade"}).notNull(),
    ...timestamps
  },
  (table) => [
    t.uniqueIndex("event_tags_event_tag_uq_idx").on(table.eventId, table.tagId),
    t.index("event_tags_event_id_idx").on(table.eventId),
    t.index("event_tags_tag_id_idx").on(table.tagId),
  ]
);
